import ErrorOutlineOutlinedIcon from '@material-ui/icons/ErrorOutlineOutlined'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import {
  LayerMap,
  MergedLayerMap,
} from '@/hooks/queries/use-get-infestation-from-diagnosis'
import { EquipmentFormValue } from '@/pages/diagnosis-view/contexts/equipment-definition-form'
import {
  createPulverization,
  CreatePulverizationPayload,
} from '@/services/create-pulverization'
import { queryClient } from '@/services/query-client'
import { DisplayValue } from '@/types/weed-diagnosis'

import { GET_ACTIVITIES_QUERY } from '../queries/use-get-activities'
import { GET_PULVERIZATION_ACTIVITY_BY_ID_QUERY } from '../queries/use-get-pulverization-activity-by-id'

interface UseCreatePulverizationPayload {
  layersMap: LayerMap[]
  mergedLayersMap: MergedLayerMap[]
  equipmentData: EquipmentFormValue
  intersect: boolean
}

export type UseCreatePulverization = ReturnType<typeof useCreatePulverization>

function getDisplay(display: DisplayValue) {
  if (display.model === 'no-display') return

  return display
}

export function useCreatePulverization(culture: string, demandId: string) {
  const mutation = useMutation({
    mutationFn: async ({
      layersMap,
      mergedLayersMap,
      equipmentData,
      intersect,
    }: UseCreatePulverizationPayload) => {
      const payload: CreatePulverizationPayload = {
        pulverization_layers: [
          ...layersMap
            .filter((layer) => !layer.removed && !layer.mergedName)
            .map((layer) => ({
              layers: [layer.name],
              buffer: layer.buffered ? layer.buffer : 0,
              organization: equipmentData.organization && {
                id: equipmentData.organization,
              },
              display: getDisplay(equipmentData.layersMap[layer.name]),
              herbicide_treatment: layer.herbicideTreatment,
            })),
          ...mergedLayersMap.map((layer) => ({
            layers: layer.name.split('-'),
            buffer: layer.buffered ? layer.buffer : 0,
            organization: equipmentData.organization && {
              id: equipmentData.organization,
            },
            display: getDisplay(equipmentData.mergedLayersMap[layer.name]),
            herbicide_treatment: layer.herbicideTreatment,
          })),
        ],
        pulverization_date: {
          start_date: equipmentData.pulverization_date.start_date,
          end_date:
            equipmentData.pulverization_date.end_date ||
            equipmentData.pulverization_date.start_date,
        },
        intersect,
      }

      return createPulverization(culture, demandId, payload)
    },

    onError(e: Error) {
      toast.error(
        'Ocorreu um erro ao realizar o pré processamento dos arquivos de pulverização: ' +
          e?.message,
      )
    },
    onSuccess(data) {
      const isNotShared = data.layers.some(
        (layer) => layer.display?.shared === false,
      )

      if (isNotShared) {
        toast.info(
          'Não foi possível enviar um ou mais resultados ao John Deere Operations Center, seus arquivos foram gerados e estão disponíveis para download.',
          {
            style: { backgroundColor: '#727970', width: '58rem' },
            icon: <ErrorOutlineOutlinedIcon />,
          },
        )
      }

      queryClient.invalidateQueries(GET_ACTIVITIES_QUERY)
      queryClient.invalidateQueries(GET_PULVERIZATION_ACTIVITY_BY_ID_QUERY)
    },
  })

  return mutation
}
